/**
 * /settings/integrations — connection ทั้งหมดของ workspace ที่เลือกอยู่
 *
 * การ์ดเดียว ไม่ใช่ section ต่อ platform: ขนาดจริงคือ 1-2 account ต่อ platform
 * รวมทั้งหน้า 5-10 แถว การหั่นเป็น 5 การ์ดทำให้ของ 5 แถวกินจอสองหน้า แถวพา
 * identity ของ platform ไปเอง (icon + ชื่อ + method) จึงไม่ต้องมีหัว section
 *
 * แถบสรุปด้านบนตอบคำถามหลักของหน้า — ข้อมูลถึงวันไหน — ก่อนที่ตาจะไล่ราย
 * แถว และแยก "delivery หยุด" ออกจาก "token พัง" เป็นคนละช่องเสมอ
 *
 * ตั้งใจไม่มี search และ filter: การค้นหาจาก 5 platform ไม่ช่วยอะไร
 *
 * @module components/settings/IntegrationsPanel
 */

import React, { useMemo } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { groupAccountsByPlatform } from "./groupAccountsByPlatform";
import { PlatformAccountRow } from "./PlatformAccountRow";
import { AddConnectionPicker } from "./AddConnectionPicker";
import { summarizeConnections } from "./connectionSummary";
import type { AccountHealth } from "@/services/accountHealth";
import type { PlatformAccount, PlatformConfig, PlatformType } from "@/types";

export interface IntegrationsPanelProps {
  platforms: PlatformConfig[];
  accounts: PlatformAccount[];
  /** PlatformType -> latest metric date delivered (from fetchPlatformFrontiers) */
  frontiers: Map<PlatformType, string>;
  /** Platforms whose frontier lookup failed — reported apart from dormant. */
  unreadableFrontiers?: ReadonlySet<PlatformType>;
  /** "today" on the DATA calendar (Asia/Bangkok) — pass todayDataDate() */
  todayIso: string;
  syncingAccountIds: string[];
  /** live-test results keyed by account id */
  health?: Record<string, AccountHealth | undefined>;
  /** account ids with a live test in flight */
  testingAccountIds?: string[];
  /**
   * accountId -> why that account's last finished sync wrote nothing, from
   * `fetchEmptySyncReasons`.
   *
   * Keyed per ACCOUNT, unlike `frontiers` beside it, and that is load-bearing:
   * the frontier is per platform, so a second Facebook account that found
   * nothing borrows its sibling's fresh date and the row says nothing is wrong.
   * This note is the only thing that explains that row.
   */
  emptySyncReasons?: ReadonlyMap<string, string>;
  onConnect: (platform: PlatformConfig) => void;
  onSync: (accountId: string) => void;
  onReconnect: (account: PlatformAccount) => void;
  onDisconnect: (accountId: string) => void;
  onTest?: (account: PlatformAccount) => void;
  onEdit?: (account: PlatformAccount) => void;
}

// วันสั้นไม่มีปีสำหรับแถบสรุป — เหตุผลเดียวกับใน PlatformAccountRow
const shortDate = (isoDate: string, language: string): string =>
  new Intl.DateTimeFormat(language === "th" ? "th-TH" : "en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T00:00:00Z`));

export const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({
  platforms,
  accounts,
  frontiers,
  unreadableFrontiers,
  todayIso,
  syncingAccountIds,
  health,
  testingAccountIds,
  emptySyncReasons,
  onConnect,
  onSync,
  onReconnect,
  onDisconnect,
  onTest,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();

  const { sections, unconnected } = useMemo(
    () => groupAccountsByPlatform(platforms, accounts),
    [platforms, accounts]
  );

  // แถวเรียงตามกลุ่ม platform เดิม แต่แบนเป็นลิสต์เดียว
  const rows = useMemo(
    () =>
      sections.flatMap((section) =>
        section.accounts.map((account) => ({ platform: section.platform, account }))
      ),
    [sections]
  );

  // demo rows ไม่มี frontier และไม่มี lastSyncAt — ปล่อยเข้าสรุปจะกลายเป็น
  // "1 ไม่มี delivery" ปลอมทั้งที่มันไม่เคยต้องส่งข้อมูล
  const summary = useMemo(
    () =>
      summarizeConnections(
        accounts.filter((a) => a.accountType !== "demo_data"),
        frontiers,
        todayIso,
        unreadableFrontiers ?? new Set()
      ),
    [accounts, frontiers, todayIso, unreadableFrontiers]
  );

  const isEmpty = sections.length === 0;

  if (isEmpty) {
    return (
      // ไม่มี section เลย → ยก picker ขึ้นมาเป็นเนื้อหาหลัก พร้อมบอกว่าต่อแล้วได้อะไร
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-6 py-10 text-center space-y-4">
        {/* h2 เพราะหน้ามี h1 เดียว (hero ใน Settings.tsx) */}
        <h2 className="text-base font-semibold text-ink-900">
          {t('settings.connectFirstAccount')}
        </h2>
        <p className="text-sm text-neutral-700 max-w-md mx-auto">
          {t('settings.connectFirstBody')}
        </p>
        <div className="flex justify-center">
          {/* หัวข้อปิดไว้: บล็อกนี้มี "Connect your first ad account" อยู่แล้ว */}
          <AddConnectionPicker
            platforms={unconnected}
            onConnect={onConnect}
            showHeading={false}
          />
        </div>
      </div>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-neutral-200">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h2 className="text-sm font-semibold text-ink-900">
          {t("settings.connectedPlatforms")}
        </h2>
        <p className="text-xs text-neutral-700 mt-0.5">{t("settings.legend")}</p>
      </div>

      {/* แถบสรุป — delivery กับ token แยกช่องกันเสมอ */}
      <div className="grid grid-cols-3 divide-x divide-neutral-100 border-b border-neutral-100 bg-neutral-50/50">
        <div className="px-4 py-2.5">
          <span className="text-lg font-semibold text-ink-900">{summary.freshCount}</span>
          <span className="ml-2 text-xs text-neutral-700">{t("settings.stripFresh")}</span>
        </div>
        <div className="px-4 py-2.5">
          <span
            className={
              summary.dormant.length > 0
                ? "text-lg font-semibold text-amber-700"
                : "text-lg font-semibold text-ink-900"
            }
          >
            {summary.dormant.length}
          </span>
          <span className="ml-2 text-xs text-neutral-700">
            {summary.unknown.length > 0
              ? t("settings.stripUnknown", { count: summary.unknown.length })
              : summary.dormant.length > 0
              ? summary.dormant
                  .map((d) =>
                    d.since
                      ? `${t("settings.stripDormantSince", { date: shortDate(d.since, i18n.language) })}`
                      : t("settings.noDataYet")
                  )
                  .join(" · ")
              : t("settings.stripDormant")}
          </span>
        </div>
        <div className="px-4 py-2.5">
          <span
            className={
              summary.tokenIssues > 0
                ? "text-lg font-semibold text-red-700"
                : "text-lg font-semibold text-ink-900"
            }
          >
            {summary.tokenIssues}
          </span>
          <span className="ml-2 text-xs text-neutral-700">{t("settings.stripTokenIssues")}</span>
        </div>
      </div>

      <div className="divide-y divide-neutral-100">
        {rows.map(({ platform, account }) => (
          <PlatformAccountRow
            key={account.id}
            account={account}
            platform={platform}
            dataThrough={frontiers.get(account.platform) ?? null}
            frontierUnreadable={unreadableFrontiers?.has(account.platform) ?? false}
            todayIso={todayIso}
            isSyncing={syncingAccountIds.includes(account.id)}
            health={health?.[account.id]}
            isTesting={testingAccountIds?.includes(account.id)}
            emptySyncReason={emptySyncReasons?.get(account.id) ?? null}
            onSync={onSync}
            onReconnect={onReconnect}
            onDisconnect={onDisconnect}
            onTest={onTest}
            onEdit={onEdit}
          />
        ))}
      </div>

      {/* แถวเดียวสำหรับต่อเพิ่มทุก platform — ต่อซ้ำ platform เดิมก็ผ่านทางนี้ */}
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 px-4 py-2.5 border-t border-neutral-100 text-sm">
        <span className="inline-flex items-center gap-1.5 text-neutral-700">
          <Plus size={14} aria-hidden="true" />
          {t("settings.addAccount")} —
        </span>
        {platforms.map((platform, i) => (
          <React.Fragment key={platform.type}>
            {i > 0 && <span className="text-neutral-700">·</span>}
            <button
              type="button"
              onClick={() => onConnect(platform)}
              className="px-1.5 py-0.5 rounded text-ink-900 underline hover:bg-neutral-50 font-medium"
            >
              {platform.displayName}
            </button>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default IntegrationsPanel;
